import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Sparkles, CalendarHeart, Users2, HeartHandshake } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const HOW_HEARD = ["Instagram", "YouTube", "Friend / Family", "Google Search", "Walked by", "Other"];

export default function FirstVisit() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    planned_date: "",
    party_size: 1,
    how_heard: "",
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.planned_date) delete payload.planned_date;
      if (!payload.how_heard) delete payload.how_heard;
      if (!payload.notes) delete payload.notes;
      payload.party_size = Number(form.party_size) || 1;
      await api.post("/visitors", payload);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    }
  };

  return (
    <section
      data-testid="first-visit-section"
      id="plan-visit"
      className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden border-y border-white/5 bg-[#0a0a0d] scroll-mt-24"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#ff5c00]/10 blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#ffe600]/10 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="lg:col-span-5 space-y-6"
        >
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00]">
            New Here?
          </p>
          <h2 className="font-display text-4xl md:text-6xl uppercase font-bold tracking-tighter leading-[0.95]">
            We're <span className="text-[#ff5c00]">saving</span> you a seat.
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed">
            Tell us you're coming and we'll have someone ready to welcome you,
            walk you in, and answer any questions. We meet at Speedex
            Building, Abubaker Al Siddique Metro — exit 1.
          </p>
          <ul className="space-y-3 pt-2">
            <li className="flex items-start gap-3 text-zinc-300">
              <CheckCircle2 className="w-5 h-5 text-[#ff5c00] mt-0.5 shrink-0" />
              <span>A friendly first-timer host meets you at the door</span>
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <CheckCircle2 className="w-5 h-5 text-[#ff5c00] mt-0.5 shrink-0" />
              <span>Clear directions sent to your email — metro & parking</span>
            </li>
            <li className="flex items-start gap-3 text-zinc-300">
              <CheckCircle2 className="w-5 h-5 text-[#ff5c00] mt-0.5 shrink-0" />
              <span>Coffee on us after service — so we can actually meet</span>
            </li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-7 glass rounded-sm p-7 md:p-10"
        >
          {status === "success" ? (
            <div data-testid="first-visit-success" className="text-center py-10 space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#ff5c00]/20 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-[#ff5c00]" />
              </div>
              <h3 className="font-display text-3xl uppercase tracking-tight">You're In!</h3>
              <p className="text-zinc-300 max-w-md mx-auto">
                We can't wait to meet you. Check your inbox for directions and
                what to expect. See you Sunday at <span className="text-[#ff5c00]">9AM or 11AM</span>.
              </p>
              <button
                onClick={() => {
                  setStatus("idle");
                  setForm({ name: "", email: "", phone: "", planned_date: "", party_size: 1, how_heard: "", notes: "" });
                }}
                data-testid="first-visit-reset"
                className="mt-4 inline-flex items-center gap-2 border border-white/20 hover:border-[#ff5c00] hover:text-[#ff5c00] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-5 py-3 rounded-sm transition-colors"
              >
                Plan Another Visit
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="first-visit-form" className="space-y-5">
              <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2">
                <CalendarHeart className="w-3 h-3 text-[#ff5c00]" /> Plan Your First Visit
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Full Name</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="first-visit-name"
                    className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Email</span>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="first-visit-email"
                    className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Phone (optional)</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="first-visit-phone"
                    className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Which Sunday?</span>
                  <input
                    type="date"
                    value={form.planned_date}
                    onChange={(e) => setForm({ ...form, planned_date: e.target.value })}
                    data-testid="first-visit-date"
                    className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block flex items-center gap-2">
                    <Users2 className="w-3 h-3" /> How many of you?
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={form.party_size}
                    onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                    data-testid="first-visit-party"
                    className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
                  />
                </label>
                <label className="block">
                  <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">How'd you hear about us?</span>
                  <select
                    value={form.how_heard}
                    onChange={(e) => setForm({ ...form, how_heard: e.target.value })}
                    data-testid="first-visit-how-heard"
                    className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
                  >
                    <option value="">Select…</option>
                    {HOW_HEARD.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Anything we should know? (optional)</span>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="first-visit-notes"
                  placeholder="Questions, prayer needs, or just say hi"
                  className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm resize-none"
                />
              </label>

              {error && <p data-testid="first-visit-error" className="text-red-400 text-sm">{error}</p>}

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="first-visit-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-8 py-4 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-10px_rgba(255,92,0,0.7)]"
                >
                  {status === "loading" ? "Saving your seat…" : "Save My Seat"}
                </button>
                <Link
                  to="/contact"
                  data-testid="first-visit-prayer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#ff5c00] hover:text-[#ff5c00] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-colors"
                >
                  <HeartHandshake className="w-4 h-4" /> Request Prayer
                </Link>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
